import { useEffect, useRef, useState } from "react"
import type { ItemInput } from "../types"
import { fetchProductPreview, PreviewsBusyError } from "../api/productPreviews"
interface Field {
  value: string
  autofilled: boolean
  edited?: boolean
}

function isWebUrl(value: string) {
  return /^https?:\/\/\S+$/i.test(value)
}

function autofill(field: Field, value: string | null | undefined) {
  if (field.edited || !value || (field.value.trim() && !field.autofilled)) return field
  return { value, autofilled: true }
}

function typedIn(value: string) {
  return { value, autofilled: false, edited: true }
}

export function useProductPreview(initialValues: Partial<ItemInput>) {
  const [name, setName] = useState<Field>({ value: initialValues.name || "", autofilled: false })
  const [url, setUrl] = useState(initialValues.url || "")
  const [imageUrl, setImageUrl] = useState(
    { value: initialValues.image_url || "", autofilled: false } as Field
  )
  const [previewStatus, setPreviewStatus] = useState<string | null>(null)
  const previewedUrl = useRef(initialValues.url || "")
  const latestPreview = useRef(0)

  function handleUrlBlur() {
    const link = url.trim()
    if (link === previewedUrl.current || !isWebUrl(link)) return

    void lookUpProduct(link)
  }

  async function lookUpProduct(link: string) {
    previewedUrl.current = link
    const previewNumber = ++latestPreview.current
    setPreviewStatus("looking")

    const { preview, busy } = await fetchProductPreview(link)
      .then((preview) => ({ preview, busy: false }))
      .catch((error) => ({
        preview: null,
        busy: error instanceof PreviewsBusyError,
      }))
    if (previewNumber !== latestPreview.current) return
    if (busy) return setPreviewStatus("busy")

    setPreviewStatus(preview?.title || preview?.image_url ? "found" : "missed")
    setName((current) => autofill(current, preview?.title))
    setImageUrl((current) => autofill(current, preview?.image_url))
  }

  useEffect(
    () => () => {
      latestPreview.current++
    },
    []
  )
  return {
    name,
    setName,
    url,
    setUrl,
    imageUrl,
    setImageUrl,
    previewStatus,
    handleUrlBlur,
    retry: () => lookUpProduct(previewedUrl.current),
    typedIn,
  }
}
